/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */

import './LinkedBankAccountSummary.scss';

import { capitalize, noop } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';

import AccountsTableByType from 'components/shared/AccountsTableByType/AccountsTableByType';
import BankAccountDetails from 'components/shared/BankAccountDetails/BankAccountDetails';
import PreferencesSection from 'components/layouts/Preferences/shared/PreferencesSection';
import AutoVerify from './AutoVerify';
import { bool, func, number, object } from 'prop-types';
import { NON_BREAKING_SPACE } from 'services/Formats';
import ChallengeDeposit from 'components/layouts/Preferences/LinkedBankAccounts/ChallengeDeposit';
import {
  BANK_RESTRICTIONS,
  BANK_RESTRICTION_MAP,
  isReadyForValidation,
  isLapsed,
  isReinitiated,
  isInitiated,
  isInitiationFail,
  isFailed,
  isDepositFail,
  isVerified
} from 'services/LinkedBankAccounts';
import { ReactComponent as WarningTriangle } from 'assets/icons/bold-warning-triangle.svg';
import ActionButton from 'components/shared/Buttons/ActionButton';
import SimpleBanner from 'components/shared/Banner/SimpleBanner';
import Content from 'services/Content';
import { getENV, TRP_ENVIRONMENTS } from 'services/globals';
import { isHiddenAccount } from 'services/Accounts';

const LinkedBankAccountSummaryCard = ({
  bankAccount,
  onClickReinitiateChallengeDeposits,
  onClickDelete,
  index,
  isSalesforceRepEnabled,
  onClickUpdate,
  showStopTransferBanner,
  hasMaryland529Only,
  hasAlaska529Only,
  hasNational529Only
}) => {
  const {
    bankAssociations,
    accountTypeName,
    bankName,
    bankVerificationStatus,
    bankRestrictions,
    isEligibleForUpdate,
    justAdded
  } = bankAccount;

  const showDepositChallenge =
    isReadyForValidation(bankVerificationStatus) ||
    (!isVerified(bankVerificationStatus) && !isFailed(bankVerificationStatus) && isSalesforceRepEnabled);
  const showVerificationLapsed = isLapsed(bankVerificationStatus);
  const showPreparingDeposits =
    isInitiated(bankVerificationStatus) ||
    isReinitiated(bankVerificationStatus);
  const showErrorBanner =
    isDepositFail(bankVerificationStatus) ||
    isInitiationFail(bankVerificationStatus);
  const showVerificationFailedBanner = isFailed(bankVerificationStatus);
  const hasBankRestriction = bankRestrictions && bankRestrictions.length > 0;
  const restrictions = bankRestrictions
    ? bankRestrictions.map((r) => r && r.restriction && r.restriction.id)
    : [];
  const showUpdate = isEligibleForUpdate;
  const showDelete = !(
    hasBankRestriction && restrictions.some((r) => BANK_RESTRICTION_MAP[r])
  );
  const allAccountsHidden = bankAssociations.every((a) => isHiddenAccount(a));

  const isEligibleToReinitiateChallengeDeposits =
    isSalesforceRepEnabled &&
    (isInitiated(bankVerificationStatus) ||
      isReinitiated(bankVerificationStatus) ||
      isDepositFail(bankVerificationStatus) ||
      isReadyForValidation(bankVerificationStatus) ||
      isFailed(bankVerificationStatus));

  const isNonProd = getENV() !== TRP_ENVIRONMENTS.PRODUCTION;

  return (
    <PreferencesSection className="bank-summary" fullBackground key={index}>
      <div className="bank-header">
        <div className="bank-title">
          {bankName}
          <p className="bank-type">{capitalize(accountTypeName)}</p>
          {justAdded && <div className="just-added-label">Just Added</div>}
        </div>
        <ul className="bank-link-actions-container">
          {isEligibleToReinitiateChallengeDeposits && (
            <li className="bank-update-button bank-link-button">
              <ActionButton
                className="update-button"
                onClick={() => onClickReinitiateChallengeDeposits(bankAccount)}
                id={`${bankName}-bank-update-button`}>
                Reinitiate challenge deposits
              </ActionButton>
            </li>
          )}
          {showUpdate && (
            <li className="bank-update-button bank-link-button">
              <ActionButton
                className="update-button"
                onClick={() => onClickUpdate(bankAccount)}
                id={`${bankName}-bank-update-button`}>
                Update
              </ActionButton>
            </li>
          )}
          {showDelete && (
            <li className="bank-update-button bank-link-button">
              <ActionButton
                className="delete-button"
                onClick={() => onClickDelete(bankAccount)}
                id={`${bankName}-bank-delete-button`}>
                Delete
              </ActionButton>
            </li>
          )}
        </ul>
      </div>

      {hasBankRestriction &&
        restrictions.map((restriction, i) => (
          <SimpleBanner
            className="warning-banner"
            key={i}
            icon={<WarningTriangle />}
            content={BANK_RESTRICTIONS[BANK_RESTRICTION_MAP[restriction]]}
          />
        ))}

      {showStopTransferBanner && !hasAlaska529Only && !hasMaryland529Only && (
        <SimpleBanner
          className="warning-banner"
          content={Content.stopTransferBannerDisclaimer}
        />
      )}

      {showStopTransferBanner && hasMaryland529Only && (
        <SimpleBanner
          className="warning-banner"
          content={Content.stopTransferBannerDisclaimerMDOnly}
        />
      )}

      {showStopTransferBanner && hasAlaska529Only && (
        <SimpleBanner
          className="warning-banner"
          content={Content.stopTransferBannerDisclaimerAKOnly}
        />
      )}

      {showVerificationLapsed &&
        !showStopTransferBanner &&
        !hasAlaska529Only &&
        !hasMaryland529Only &&
        !hasNational529Only && (
          <SimpleBanner
            className="warning-banner"
            content={Content.verificationLapsedBannerDisclaimer}
          />
        )}

      {showVerificationLapsed &&
        !showStopTransferBanner &&
        hasAlaska529Only && (
          <SimpleBanner
            className="warning-banner"
            content={Content.verificationLapsedBannerDisclaimerAKOnly}
          />
        )}

      {showVerificationLapsed &&
        !showStopTransferBanner &&
        hasMaryland529Only && (
          <SimpleBanner
            className="warning-banner"
            content={Content.verificationLapsedBannerDisclaimerMDOnly}
          />
        )}

      {showVerificationLapsed &&
        !showStopTransferBanner &&
        hasNational529Only && (
          <SimpleBanner
            className="warning-banner"
            content={Content.verificationLapsedBannerDisclaimerNATOnly}
          />
        )}

      {showVerificationFailedBanner &&
        !showStopTransferBanner && (
          <SimpleBanner
            className="warning-banner"
            content={Content.verificationFailedBannerDisclaimer}
          />
        )}

      {showErrorBanner &&
        !showStopTransferBanner &&
        !hasAlaska529Only &&
        !hasMaryland529Only &&
        !hasNational529Only && (
          <SimpleBanner
            className="warning-banner"
            content={Content.bankFailedDisclaimer}
          />
        )}

      {showErrorBanner && !showStopTransferBanner && hasAlaska529Only && (
        <SimpleBanner
          className="warning-banner"
          content={Content.bankFailedDisclaimerAKOnly}
        />
      )}

      {showErrorBanner && !showStopTransferBanner && hasMaryland529Only && (
        <SimpleBanner
          className="warning-banner"
          content={Content.bankFailedDisclaimerMDOnly}
        />
      )}

      {showErrorBanner && !showStopTransferBanner && hasNational529Only && (
        <SimpleBanner
          className="warning-banner"
          content={Content.bankFailedDisclaimerNATOnly}
        />
      )}

      {showPreparingDeposits && !showStopTransferBanner && (
        <SimpleBanner
          className="warning-banner"
          content={Content.preparingMicroDeposits}
        />
      )}

      {isNonProd && <AutoVerify bankAccount={bankAccount} />}

      {showDepositChallenge && <ChallengeDeposit bankAccount={bankAccount} />}

      <BankAccountDetails bankAccountDetails={bankAccount} />

      {allAccountsHidden && bankAssociations.length > 0 && (
        <div className="all-hidden-text">
          All of the Accounts associated to this bank are{NON_BREAKING_SPACE}
          <Link to="customize-account-view">hidden</Link>
        </div>
      )}

      {bankAssociations.length === 0 && (
        <div className="all-hidden-text">
          <WarningTriangle className="warning-triangle" />
          There are no accounts associated with this bank.
        </div>
      )}

      {!allAccountsHidden && bankAssociations.length > 0 && (
        <AccountsTableByType
          accounts={bankAssociations}
          bankRestrictions={bankRestrictions}
          leftColTitle={'Linked Investments'}
          rightColTitle={'Account Number'}
        />
      )}
    </PreferencesSection>
  );
};

LinkedBankAccountSummaryCard.propTypes = {
  buttonsDisabled: bool,
  bankAccount: object.isRequired,
  onClickDelete: func.isRequired,
  index: number.isRequired,
  onClickUpdate: func.isRequired
};

LinkedBankAccountSummaryCard.defaultProps = {
  buttonsDisabled: false,
  bankAccount: {},
  onClickDelete: noop,
  index: 0,
  onClickUpdate: noop
};

export default LinkedBankAccountSummaryCard;
